import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { SelectedFileBanner } from "components/SelectedFileBanner";
import { ChangeEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

type InlineFilePickerProps = {
  acceptedExtensions?: string[];
  className?: string;
  disabled?: boolean;
  file: File | null;
  onFileChange: (file: File | null) => void;
};

export const InlineFilePicker = ({
  acceptedExtensions,
  className,
  disabled,
  file,
  onFileChange,
}: InlineFilePickerProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    onFileChange(next);
    event.target.value = "";
  };

  const handleOpen = () => {
    if (!disabled) inputRef.current?.click();
  };

  const input = (
    <input
      accept={acceptedExtensions?.join(",")}
      className="hidden"
      onChange={handleChange}
      ref={inputRef}
      type="file"
    />
  );

  if (file) {
    return <SelectedFileBanner fileName={file.name} onRemove={() => onFileChange(null)} />;
  }

  return (
    <Button
      className={cn(
        "relative flex !h-12 w-full items-center rounded-md  !bg-lds-surface-muted !border-ds-subtle",
        className
      )}
      disabled={disabled}
      onClick={handleOpen}
      variant="secondary"
    >
      <Stack className="h-full w-full gap-3">
        <Button variant="secondary">{t("general.chooseFile")}</Button>
        <Stack className="gap-1">
          <span className="truncate text-lds-foreground-secondary font-ds-body-semibold-sm">
            {t("Execution.run.noFileChosen")}
          </span>
        </Stack>
      </Stack>
      {input}
    </Button>
  );
};
